<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-4xl mx-auto w-full space-y-6">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Question Details
                </h1>
                <router-link
                    to="/question-banks"
                    class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    ← Back to Questions
                </router-link>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center py-8">
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Question Details -->
            <div
                v-else-if="question"
                class="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
            >
                <div class="space-y-4">
                    <!-- Question Header -->
                    <div class="flex items-start justify-between">
                        <div class="flex-1">
                            <div class="flex items-center gap-2 mb-2">
                                <span
                                    class="px-2 py-1 text-xs font-medium rounded-full"
                                    :class="getTypeBadgeClass(question.type)"
                                >
                                    {{ formatQuestionType(question.type) }}
                                </span>
                                <span
                                    class="px-2 py-1 text-xs font-medium rounded-full"
                                    :class="
                                        getDifficultyBadgeClass(
                                            question.difficulty,
                                        )
                                    "
                                >
                                    {{ formatDifficulty(question.difficulty) }}
                                </span>
                                <span
                                    class="px-2 py-1 text-xs font-medium rounded-full"
                                    :class="
                                        question.is_active
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                    "
                                >
                                    {{
                                        question.is_active
                                            ? "Active"
                                            : "Inactive"
                                    }}
                                </span>
                            </div>
                            <h3
                                class="text-lg font-medium text-gray-900 dark:text-white mb-2"
                            >
                                {{ question.question }}
                            </h3>

                            <!-- Question Image -->
                            <div
                                v-if="question.image_url"
                                class="mt-4 mb-2 rounded-lg overflow-hidden"
                            >
                                <img
                                    :src="question.image_url"
                                    alt="Question image"
                                    class="max-w-full h-auto rounded-lg"
                                />
                            </div>
                            <div
                                class="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400"
                            >
                                <span>Points: {{ question.points }}</span>
                                <span
                                    >Teacher:
                                    {{ question.teacher?.user?.name }}</span
                                >
                                <span
                                    >Subject: {{ question.subject?.name }}</span
                                >
                            </div>
                        </div>
                        <div class="flex gap-2">
                            <router-link
                                :to="`/question-banks/${question.id}/edit`"
                                class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                            >
                                Edit
                            </router-link>
                            <button
                                @click="handleDelete"
                                class="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                            >
                                Delete
                            </button>
                        </div>
                    </div>

                    <!-- Tags -->
                    <div
                        v-if="question.tags && question.tags.length > 0"
                        class="flex flex-wrap gap-1"
                    >
                        <span
                            v-for="tag in question.tags"
                            :key="tag"
                            class="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded text-xs"
                        >
                            {{ tag }}
                        </span>
                    </div>

                    <!-- Options -->
                    <div
                        v-if="question.options && question.options.length > 0"
                        class="space-y-2"
                    >
                        <h4 class="font-medium text-gray-900 dark:text-white">
                            Options:
                        </h4>
                        <div class="space-y-2">
                            <div
                                v-for="option in question.options"
                                :key="option.id"
                                class="flex items-center gap-2 p-2 rounded"
                                :class="
                                    option.is_correct
                                        ? 'bg-green-50 dark:bg-green-900/20'
                                        : 'bg-gray-50 dark:bg-gray-700/20'
                                "
                            >
                                <span
                                    class="font-medium text-gray-700 dark:text-gray-300"
                                >
                                    {{ option.option_key }}.
                                </span>
                                <span
                                    class="flex-1 text-gray-900 dark:text-white"
                                >
                                    {{ option.option_text }}
                                </span>
                                <span
                                    v-if="option.is_correct"
                                    class="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 rounded text-xs"
                                >
                                    Correct
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Explanation -->
                    <div v-if="question.explanation" class="space-y-2">
                        <h4 class="font-medium text-gray-900 dark:text-white">
                            Explanation:
                        </h4>
                        <p class="text-gray-700 dark:text-gray-300">
                            {{ question.explanation }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Error State -->
            <div
                v-else
                class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center"
            >
                <p class="text-gray-500 dark:text-gray-400">
                    Question not found.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuestionBankStore } from "@/stores/questionBankStore";
import NotificationService from "@/utils/notifications";
import type { QuestionBank } from "@/types/questionBank";

const route = useRoute();
const router = useRouter();
const questionBankStore = useQuestionBankStore();

// State
const loading = ref(true);
const question = ref<QuestionBank | null>(null);

// Methods
const loadQuestion = async () => {
    const questionId = route.params.id as string;

    try {
        loading.value = true;
        question.value = await questionBankStore.fetchQuestion(questionId);
    } catch (error) {
        console.error("Failed to load question:", error);
        NotificationService.error("Failed to load question");
    } finally {
        loading.value = false;
    }
};

const handleDelete = async () => {
    if (!question.value) return;

    if (confirm("Are you sure you want to delete this question?")) {
        try {
            await questionBankStore.deleteQuestion(question.value.id);
            NotificationService.success("Question deleted successfully");
            router.push("/question-banks");
        } catch (error) {
            console.error("Failed to delete question:", error);
            NotificationService.error("Failed to delete question");
        }
    }
};

const formatQuestionType = (type: string) => {
    const types: Record<string, string> = {
        multiple_choice: "Multiple Choice",
        multiple_select: "Multiple Select",
        true_false: "True/False",
        essay: "Essay",
    };
    return types[type] || type;
};

const formatDifficulty = (difficulty: string) => {
    const difficulties: Record<string, string> = {
        easy: "Easy",
        medium: "Medium",
        hard: "Hard",
    };
    return difficulties[difficulty] || difficulty;
};

const getTypeBadgeClass = (type: string) => {
    const classes: Record<string, string> = {
        multiple_choice:
            "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
        multiple_select:
            "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
        true_false:
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
        essay: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    };
    return (
        classes[type] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
    );
};

const getDifficultyBadgeClass = (difficulty: string) => {
    const classes: Record<string, string> = {
        easy: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
        medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
        hard: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    };
    return (
        classes[difficulty] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
    );
};

// Lifecycle
onMounted(() => {
    loadQuestion();
});
</script>
